/* eslint-disable react/no-unescaped-entities */
import { FaMinus, FaPlus } from 'react-icons/fa';
import {
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useMemo,
  useState,
} from 'react';
import Tippy from '@tippyjs/react';

interface AccordionProps extends PropsWithChildren {
  title: string;
  items?: { id: string | number; name: string }[];
  enableTooltip?: boolean;
  defaultOpen?: boolean;
  // Controlled state props
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  addorment?: {
    startAddorment?: ReactNode;
    endAddorment?: ReactNode;
  };
  variant?: 'gray' | 'default';
}

const Accordion: FC<AccordionProps> = ({
  title,
  children,
  items = [],
  enableTooltip = false,
  defaultOpen = false,
  open,
  onOpenChange,
  addorment,
  variant = 'default',
}) => {
  const hasContent = useMemo(() => {
    if (children !== undefined || items.length > 0) return true;
    return false;
  }, [children, items]);

  // Determine if we're in controlled mode
  const isControlled = open !== undefined;

  // Internal state for uncontrolled mode
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  // Use controlled state if available, otherwise internal state
  const isOpen = isControlled ? open : internalOpen;

  // Handle toggle for both controlled and uncontrolled modes
  const handleToggle = (): void => {
    if (isControlled) {
      onOpenChange?.(!open);
    } else {
      setInternalOpen((prev) => !prev);
    }
  };

  return (
    <div className="w-full">
      <div
        className={`mx-auto w-full rounded-2xl  p-4 ${
          variant === 'default' ? 'bg-menu-light-purple' : 'bg-gray-5'
        }`}
        data-headlessui-state={isOpen ? 'open' : 'closed'}
      >
        <div>
          <button
            className={`flex w-full justify-between items-center rounded-lg text-left text-base focus:outline-none ${
              hasContent ? 'cursor-pointer' : 'cursor-default'
            }`}
            onClick={hasContent ? handleToggle : undefined}
            type="button"
          >
            <Tippy content={title} maxWidth={700} disabled={!enableTooltip}>
              <span className="text-gray-55 font-bold truncate flex gap-1">
                {addorment?.startAddorment}
                {title}
                {addorment?.endAddorment}
              </span>
            </Tippy>
            {hasContent && (
              <div className="text-secondary-dark">
                {isOpen ? <FaMinus size={12} /> : <FaPlus size={12} />}
              </div>
            )}
          </button>

          {hasContent && isOpen && (
            <div className="text-sm text-gray-50 pt-4">
              {children}
              {items.length > 0 && (
                <div className="flex flex-col gap-2">
                  {items?.map((item) => <div key={item.id}>{item.name}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Accordion;
